import { getAuth, createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { doc, setDoc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { COLLECTIONS } from '../models/database';

/**
 * Create a new admin user
 * @param {string} email - User email
 * @param {string} password - User password
 * @param {string} displayName - User display name
 * @returns {Promise<object>} Created user object
 */
export const createAdminUser = async (email, password, displayName) => {
  try {
    const auth = getAuth();
    
    // Create user with Firebase Authentication
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // Update user profile with display name
    await updateProfile(user, { displayName });
    
    // Create user document in Firestore
    await setDoc(doc(db, COLLECTIONS.USERS, user.uid), {
      email,
      displayName,
      role: 'admin',
      createdAt: new Date(),
      updatedAt: new Date(),
    });
    
    return user;
  } catch (error) {
    console.error('Error creating admin user:', error);
    throw error;
  }
};

/**
 * Check if a user has admin role
 * @param {string} userId - User ID
 * @returns {Promise<boolean>} True if user has admin role, false otherwise
 */
export const isAdmin = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, userId));
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      return userData.role === 'admin';
    }
    
    return false;
  } catch (error) {
    console.error('Error checking admin role:', error);
    return false;
  }
};

/**
 * Get user role
 * @param {string} userId - User ID
 * @returns {Promise<string|null>} User role or null if user not found
 */
export const getUserRole = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, userId));
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      return userData.role;
    }
    
    return null;
  } catch (error) {
    console.error('Error getting user role:', error);
    return null;
  }
};

/**
 * Get all users with a specific role
 * @param {string} role - User role to filter by
 * @returns {Promise<Array>} Array of users with the specified role
 */
export const getUsersByRole = async (role) => {
  try {
    const usersRef = collection(db, COLLECTIONS.USERS);
    const q = query(usersRef, where('role', '==', role));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error(`Error getting users with role ${role}:`, error);
    return [];
  }
};

/**
 * Create an operative user
 * @param {string} email - User email
 * @param {string} password - User password
 * @param {string} displayName - User display name
 * @param {Array} practiceIds - Array of practice IDs the operative is associated with
 * @returns {Promise<object>} Created user object
 */
export const createOperativeUser = async (email, password, displayName, practiceIds = []) => {
  try {
    const auth = getAuth();
    
    // Create user with Firebase Authentication
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // Update user profile with display name
    await updateProfile(user, { displayName });
    
    // Create user document in Firestore
    await setDoc(doc(db, COLLECTIONS.USERS, user.uid), {
      email,
      displayName,
      role: 'operative',
      practices: practiceIds,
      createdAt: new Date(),
      updatedAt: new Date(),
    });
    
    return user;
  } catch (error) {
    console.error('Error creating operative user:', error);
    throw error;
  }
};

/**
 * Update user's associated practices
 * @param {string} userId - User ID
 * @param {Array} practiceIds - Array of practice IDs
 * @returns {Promise<void>}
 */
export const updateUserPractices = async (userId, practiceIds) => {
  try {
    const userRef = doc(db, COLLECTIONS.USERS, userId);
    
    await setDoc(userRef, {
      practices: practiceIds,
      updatedAt: new Date(),
    }, { merge: true });
  } catch (error) {
    console.error('Error updating user practices:', error);
    throw error;
  }
};
